import React, { useMemo, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useTestingProjects } from '../UserTesting/hooks';
import AeroHomeView from './AeroHomeView';
import { stateFromError } from '../../components/Aero/AeroStatePanel';
import { getSeenAt, countNew } from '../../utils/sessionsSeen';
import {
  buildEvents,
  groupByDay,
  sessionDots,
  testStatus,
  relativeDay,
  siteAddress,
  siteIsLive,
} from './homeModel';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';

/**
 * AeroHome: Aero's Home at "/" on aeroapp.ai (v3 phase 2, layout A).
 *
 * Data: the tests from useTestingProjects (the same read the old "/" page, the
 * Tests list, made) and the sites from the shell (AeroLayout's
 * useAeroShellData). New site and New test go to their create pages
 * (/sites/new, /user-testing/new), which use the same CreateFlow, with the
 * same actions, as the Sites and Tests pages.
 */
const byTime = (a, b) => ((b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));

export default function AeroHome() {
  const { user, planKey, shell = {} } = useOutletContext();
  const navigate = useNavigate();
  const { projects, loading, error, refetch } = useTestingProjects(user?.uid);

  useEffect(() => {
    document.title = 'Home | Aero';
  }, []);


  const shellSites = shell.sites;
  const sites = useMemo(() => shellSites || [], [shellSites]);
  const liveTests = useMemo(() => projects.filter((p) => !p.draft), [projects]);
  const liveSites = useMemo(() => sites.filter((s) => !s.draft), [sites]);

  const groups = useMemo(
    () => groupByDay(buildEvents({ projects: liveTests, sites: liveSites })),
    [liveTests, liveSites]
  );

  const siteRows = useMemo(
    () => liveSites.slice().sort(byTime).slice(0, 3).map((s) => ({
      id: s.id,
      name: s.title || s.generatedUrl,
      address: siteAddress(s),
      status: siteIsLive(s) ? 'live' : 'off',
      favicon: s.faviconUrl,
      previewUrl: s.previewUrl,
      previewColors: s.previewColors,
    })),
    [liveSites]
  );

  const testRows = useMemo(
    () => liveTests.slice(0, 4).map((p) => {
      const n = p.sessionCount || 0;
      return {
        id: p.id,
        name: p.title,
        meta: `${n === 0 ? 'No sessions yet' : `${n} session${n === 1 ? '' : 's'}`} · ${relativeDay(p.lastSessionAt ? new Date(p.lastSessionAt) : null)}`,
        dots: sessionDots(p),
        status: testStatus(p),
        newCount: countNew(p.recentSessions, getSeenAt(user?.uid, p.id)),
        previewUrl: p.previewUrl || null,
        previewColors: p.previewColors || null,
      };
    }),
    [liveTests, user]
  );

  const sitesKnown = (shellSites !== null && shellSites !== undefined) || !!shell.failed;
  let state = 'ready';
  if (loading || (!sitesKnown && !error)) state = 'loading';
  else if (error && !liveSites.length) state = 'error';
  else if (!liveTests.length && !liveSites.length && !projects.length && !sites.length) state = 'empty';

  return (
    <div className="aero-main aero-home-page">
      <AeroHomeView
        state={state}
        groups={groups}
        sites={siteRows}
        siteCount={shellSites ? liveSites.length : null}
        tests={testRows}
        testCount={loading ? null : liveTests.length}
        isFree={planKey === 'free'}
        onNewSite={(source) => navigate(source ? `/sites/new?create=${source}` : '/sites/new')}
        onNewTest={() => navigate('/user-testing/new')}
        onOpen={(path) => navigate(path)}
        onRetry={refetch}
        errorKind={error && stateFromError({ message: error }) === 'offline' ? 'offline' : 'error'}
      />

    </div>
  );
}
